import { Navigate, useLocation } from "react-router-dom";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getFirstAccessibleRoute } from "../../../lib/permissions/permissions";
import { authService } from "../../../lib/api/apiService";

/**
 * Garde de route basée sur les permissions.
 *
 *  <RequirePermission permission="products:create">
 *    <AddProduct />
 *  </RequirePermission>
 *
 *  <RequirePermission anyOf={["orders:read", "orders:update"]}>
 *    <OrderList />
 *  </RequirePermission>
 */
const RequirePermission = ({ permission, anyOf, children, fallback = null }) => {
    const location = useLocation();
    const isAuthenticated = authService.isAuthenticated();
    const { data: user, isLoading } = useCurrentUser();
    const { can, canAny } = usePermissions();

    if (!isAuthenticated) {
        return <Navigate to="/vendor/login" replace state={{ from: location }} />;
    }
    if (isLoading) return null;
    if (!user) {
        return <Navigate to="/vendor/login" replace state={{ from: location }} />;
    }

    const allowed = anyOf ? canAny(anyOf) : can(permission);

    if (!allowed) {
        // Si un fallback UI est fourni, on l'affiche (ex: page 403 inline)
        if (fallback) return fallback;
        // Sinon on redirige vers la première route accessible, ou /unauthorized
        const target = getFirstAccessibleRoute(user, "/unauthorized");
        // Évite une boucle infinie si l'utilisateur est déjà sur la cible
        if (location.pathname === target) {
            return <Navigate to="/unauthorized" replace />;
        }
        return <Navigate to={target} replace />;
    }

    return children ?? null;
};

export default RequirePermission;
